import { useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface DropzoneProps {
  /** Called with the chosen/dropped files (drag-drop or the hidden file input). */
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  /** Renders inert (e.g. a max-files limit reached). */
  disabled?: boolean;
  /** Extra classes — typically height/gap and outer spacing. */
  className?: string;
  ariaLabel?: string;
  /** The drop area's content, e.g. an icon and helper text. */
  children: ReactNode;
}

/**
 * A drag-and-drop / click-to-upload drop area. Presentational shell only —
 * validation, previews and limits belong to the caller.
 */
export function Dropzone({ onFiles, accept, multiple = false, disabled = false, className, ariaLabel, children }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const open = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div
      role="button"
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={open}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        if (!disabled) onFiles(e.dataTransfer.files);
      }}
      className={cn(
        'flex flex-col items-center justify-center rounded-input border bg-dinnerplate-white-lighter',
        disabled
          ? 'cursor-not-allowed border-dinnerplate-white opacity-50 pointer-events-none'
          : dragActive
            ? 'cursor-pointer border-off-black bg-dinnerplate-white'
            : 'cursor-pointer border-dinnerplate-white',
        className,
      )}
    >
      {children}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) onFiles(e.target.files);
          // Reset so choosing the same file again re-triggers onChange.
          e.target.value = '';
        }}
      />
    </div>
  );
}
